import { useMemo } from 'react';
import { TableColumn } from './types';

const calculateTableWidth = (columns: TableColumn[], expandedColumns: Set<string>): number => {
  let total = 0;
  for (const column of columns) {
    if (!column.children?.length) {
      total += column.width;
    } else if (expandedColumns.has(column.key)) {
      total += column.children.reduce((sum, child) => sum + child.width, 0);
    } else {
      // Use the parent width for collapsed state to keep CSS grid and minWidth in sync
      total += column.width;
    }
  }
  return total;
};

const calculateGridTemplateColumns = (
  columns: TableColumn[],
  expandedColumns: Set<string>,
): string => {
  const parts: string[] = [];
  for (const column of columns) {
    if (!column.children?.length) {
      parts.push(`minmax(${column.width}px, 1fr)`);
    } else if (expandedColumns.has(column.key)) {
      for (const child of column.children) {
        parts.push(`${child.width}px`);
      }
    } else {
      parts.push(`${column.width}px`);
    }
  }
  return parts.join(' ');
};

const useCellWidthCalculation = (columns: TableColumn[], expandedColumns: Set<string>) => {
  const gridTemplateColumns = useMemo(
    () => calculateGridTemplateColumns(columns, expandedColumns),
    [columns, expandedColumns],
  );

  const minWidth = useMemo(
    () => calculateTableWidth(columns, expandedColumns),
    [columns, expandedColumns],
  );

  return { gridTemplateColumns, minWidth };
};

export default useCellWidthCalculation;
